import React from "react";
import backgroundImg from "./../images/footer.jpg";
import webdesignImg from "./../images/webdesign.png";
import { Col, Container, Row } from "react-bootstrap";
import { phoneIcon, mailIcon } from "../images";
import { StyledButton } from "../components";
import { useWindowSize, useScrollToTopAndNavigate } from "../hooks";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface FooterProps {
  logoUrl: string;
  logoSmallUrl: string;
  company: string;
  streetAddress: string;
  postalCode: string;
  locality: string;
  mail: string;
  phone: string;
}

const Footer: React.FunctionComponent<FooterProps> = ({ logoUrl, logoSmallUrl, company, streetAddress, postalCode, locality, mail, phone }) => {
  const { t } = useTranslation("footer");
  const { width } = useWindowSize();
  const handleNavigation = useScrollToTopAndNavigate();

  const currentYear = new Date().getFullYear();

  const logoContainerStyle: React.CSSProperties = {
    background: "transparent",
    textAlign: "center",
    padding: "2rem 0",
  };

  const logoStyle: React.CSSProperties = {
    width: "250px",
    height: "auto",
  };

  const dividerStyle: React.CSSProperties = {
    borderTop: "1.5px solid #c39d63",
    margin: "2rem 0 0 0",
  };

  const footerStyle: React.CSSProperties = {
    background: `url(${backgroundImg}) no-repeat center center`,
    backgroundSize: "cover",
    color: "#fff",
    padding: "3.9rem 0",
    position: "relative",
    textAlign: "center",
  };

  const topSectionStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "2rem 0",
  };

  const linkStyle: React.CSSProperties = {
    color: "#ffffffe6",
    textDecoration: "none",
    marginBottom: "0.5rem",
    display: "block",
    fontFamily: "Avenir Next Codensed Regular"
  };

  const bottomSectionStyle: React.CSSProperties = {
    fontSize: "0.9rem",
  };

  const copyrightStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  };

  const headerStyle: React.CSSProperties = {
    color: "#c39d63",
    fontSize: "1.5rem",
    fontFamily: "Avenir Next Codensed Ultralight",
    fontWeight: 700
  }

  const subTitleStyle: React.CSSProperties = {
    fontSize: "45px",
    fontWeight: 300,
    marginBottom: 30,
    color: "rgba(255, 255, 255, 0.9)",
    textTransform: 'capitalize',
    textAlign: 'right',
    fontFamily: "Alfa Slab One",
    fontStyle: 'normal',
    lineHeight: '40px',
    letterSpacing: '.03em',
    display: 'block'
  };

  const contentStyle: React.CSSProperties = {
    fontFamily: "Avenir Next Codensed Regular",
    fontWeight: 300,
    fontSize: '20px',
    lineHeight: '24px',
    textTransform: 'capitalize',
    textAlign: 'right',
    color: 'rgba(255, 255, 255, 0.9)'
  };

  return (
    <footer>
      {/* Logo auf transparentem Hintergrund */}
      <div style={logoContainerStyle}>
        <img src={logoUrl} alt="Logo" style={logoStyle} />
      </div>
      {/* Trennlinie */}
      <div style={dividerStyle}></div>
      {/* Footer-Inhalt */}
      <div style={footerStyle}>
        <Container>
          <Row style={{ padding: 70, position: "relative" }}>
            {/* Erste Spalte */}
            <Col
              xl={6}
              lg={6}
              md={6}
              sm={12}
              xs={12}
              style={{
                borderRight: width > 767 ? "1px solid rgba(255, 255, 255, 0.9)" : 'none',
                borderBottom: width < 767 ? "1px solid rgba(255, 255, 255, 0.9)" : 'none',
                paddingBottom: width < 767 ? 30 : 'none',
                marginTop: "30px",
              }}
            >
              <div style={{ maxWidth: 450 }}>
                <div style={subTitleStyle}>{t("info_content1")}</div>
                <div style={contentStyle}>{t("info_content2")}</div>
                <div style={{ display: "flex", justifyContent: "right" }}>
                  <StyledButton
                    title={t("link_about")}
                    onClick={() => { handleNavigation("/about#section_start") }}
                    isRevert={true}
                  />
                </div>
              </div>
            </Col>

            {/* Zweite Spalte */}
            <Col
              xl={6}
              lg={6}
              md={6}
              sm={12}
              xs={12}
              style={{
                borderLeft: width > 767 ? "1px solid rgba(255, 255, 255, 0.9)" : 'none',
                paddingLeft: "30px",
                marginTop: 30,
              }}
            >
              <div style={{ maxWidth: 450 }}>
                <div style={{ display: "flex" }}>
                  <div style={{ maxWidth: 44, width: "100%" }}>
                    <img src={logoSmallUrl} alt="Logo" style={{ maxWidth: "100%", height: "auto" }} />
                  </div>
                  <div style={{ ...linkStyle, margin: "auto 0 auto 15px", fontSize: "20px", textAlign: "left" }}>
                    <strong>{company}</strong>
                    <br />
                    {streetAddress}
                    <br />
                    D-{postalCode} {locality}
                  </div>
                </div>
                <div style={{ display: "flex", marginTop: 30, cursor: 'pointer' }}>
                  <div style={{ maxHeight: 44, maxWidth: 82 }}>
                    <img src={phoneIcon} alt="Phone" style={{ maxHeight: 44, width: "auto" }} />
                  </div>
                  <div style={{ ...linkStyle, margin: "auto 0 auto 15px", fontSize: "20px", whiteSpace: 'nowrap' }}>{phone}</div>
                </div>
                <div style={{ display: "flex", marginTop: 30, cursor: 'pointer' }} onClick={() => { handleNavigation("/contact#section_start") }}>
                  <div style={{ maxHeight: 44, maxWidth: 82 }}>
                    <img src={mailIcon} alt="Mail" style={{ maxHeight: 44, width: "auto" }} />
                  </div>
                  <div style={{ ...linkStyle, margin: "auto 0 auto 15px", fontSize: "20px", whiteSpace: 'nowrap' }}>{mail}</div>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            {/* Copyright und Webdesign */}
            <Col xl={6} lg={6} md={6} sm={12} xs={12} style={bottomSectionStyle}>
              <div style={copyrightStyle}>
                <span>© {currentYear} {company}</span>
                <img src={webdesignImg} alt="Webdesign by Dennis Rupp" style={{ width: "120px" }} />
              </div>
            </Col>
            {/* Links: Impressum, Datenschutz, AGB */}
            <Col xl={6} lg={6} md={6} sm={12} xs={12} style={bottomSectionStyle}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Link
                  to="/legal-notice"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigation("/legal-notice#section_start");
                  }}
                  style={{ ...linkStyle, margin: 15 }}>
                  {t("link_notice")}
                </Link>
                <Link
                  to="/privacy"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigation("/privacy#section_start");
                  }}
                  style={{ ...linkStyle, margin: 15 }}>
                  {t("link_privacy")}
                </Link>
                <Link
                  to="/terms"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigation("/terms#section_start");
                  }}
                  style={{ ...linkStyle, margin: 15 }}>
                  {t("link_terms")}
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </footer>
  );
};

export default Footer;
